/**
 * Eyebrow — the small tracked capital label (`STYLE.md` §8).
 *
 * 11/14 `--font-display` 600, 0.14em, uppercase, `--neutral-500`. It is the label over a
 * form control, over a toolbar select, over a small group ("what each role can do"), and the
 * count beside a `SectionTitle`. `Input` and `Select` draw their own — this is for the places
 * that have a label without having a field.
 *
 * **Why it is a component and not a class.** The 2026-09-16 review found twenty-five distinct
 * `letter-spacing` values in `src/`, most of them attached to something shaped exactly like
 * this: a tiny uppercase label, tracked by whatever looked right in the file it was written
 * in. The tracking is a property of the role, not a knob (§2), so the role gets a name.
 *
 * **`as` exists because the element is a document decision, not a visual one.** The style is
 * identical whichever tag it renders; what changes is whether the text belongs to the outline
 * (`h3`, a real heading for a group), is a paragraph of its own (`p`), or is a fragment
 * sitting inside something else (`span`, the default, and what a count beside a section title
 * has to be — a heading inside a heading is not markup). Three levels exist below the page
 * title and nothing between them; a 13px uppercase label is none of them and does not exist.
 */

import type { ReactNode } from "react";

import styles from "./Typography.module.css";

export type EyebrowElement = "span" | "p" | "h3" | "div";

export interface EyebrowProps {
  /** The element to render. `span` unless the label belongs to the document outline. */
  readonly as?: EyebrowElement;
  /** Drawn uppercase by the stylesheet — pass the text in sentence case. */
  readonly children: ReactNode;
}

export function Eyebrow({ as: Element = "span", children }: EyebrowProps) {
  return <Element className={styles.eyebrow}>{children}</Element>;
}
